/**
 * One designed card for every non-question state: loading, not found,
 * unreachable, rate limited, already complete, generic error. Same card, same
 * rhythm, a small glyph, an eyebrow, a heading, a sentence or two, and at most
 * one primary action. No raw error text ever reaches the respondent.
 */

import type { ReactNode } from 'react';

export type Glyph = 'offline' | 'clock' | 'check' | 'alert' | 'missing';
export type Tone = 'neutral' | 'brand' | 'warning' | 'danger';

interface Props {
  glyph: Glyph;
  tone?: Tone;
  eyebrow: string;
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
}

const PATHS: Record<Glyph, ReactNode> = {
  // Cloud with a slash: the network is not there.
  offline: (
    <>
      <path d="M7 18h10a4 4 0 0 0 .6-7.95A6 6 0 0 0 6.2 9.1 4.5 4.5 0 0 0 7 18Z" />
      <path d="M4 4l16 16" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  check: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12 2.5 2.5 4.5-5" />
    </>
  ),
  alert: (
    <>
      <path d="M12 4 3 20h18L12 4Z" />
      <path d="M12 10v4" />
      <path d="M12 17.5v.5" />
    </>
  ),
  missing: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 1-1 1.7" />
      <path d="M12 17v.5" />
    </>
  ),
};

export function StatusScreen({ glyph, tone = 'neutral', eyebrow, title, children, actions }: Props) {
  return (
    <div className={`card status tone-${tone}`} role="status" aria-live="polite">
      <div className="status-mark" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          {PATHS[glyph]}
        </svg>
      </div>
      <p className="status-eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
      {children}
      {actions ? <div className="actions">{actions}</div> : null}
    </div>
  );
}

/** The shape of the card that is about to arrive, while a stored id is verified. */
export function SkeletonCard() {
  return (
    <div className="card" role="status" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading</span>
      <div className="skeleton-line w-40" />
      <div className="skeleton-line" style={{ height: 8, marginBottom: 24 }} />
      <div className="skeleton-line w-80" />
      <div className="skeleton-line w-60" />
      <div className="skeleton-line tall" />
      <div className="skeleton-line tall" />
      <div className="skeleton-line tall" />
      <div className="skeleton-line control" />
    </div>
  );
}
